/**
 * src/components/spatial/chatRichText.ts
 *
 * Turning one chat message into the few pieces worth rendering.
 *
 * Chat bubbles rendered message text as a single text node, which is the
 * right default and was wrong for exactly one case: a model describing an
 * NFT wrote `![Stellar Collection](https://…/art.png)`, and a visitor
 * read the punctuation. The picture was the answer to the question they
 * asked, and it arrived as characters.
 *
 * So this splits a message into text, links and images — and nothing
 * else. It is deliberately not a markdown renderer:
 *
 * **No HTML, ever.** Markdown is parsed into a small list of typed
 * segments that the component maps to React elements. There is no
 * `dangerouslySetInnerHTML` anywhere in the path, so a message cannot
 * introduce markup no matter what it contains. Everything unrecognised
 * stays literal text, which is the safe direction to fail in: a visitor
 * seeing an asterisk is a cosmetic problem, and a visitor rendering
 * someone else's markup is not.
 *
 * **Only `https:`.** A URL with any other scheme is left as text rather
 * than linked. That rules out `javascript:` without needing to reason
 * about it, and also `http:`, because a mixed-content image fails
 * silently anyway.
 *
 * **An image is a claim that has to survive contact with the network.**
 * The URL in a message may not resolve — the NFT that prompted this
 * pointed at `example.com`, a reserved domain that can never serve a
 * picture. So an image segment carries its alt text and its href, and
 * the component falls back to a link when loading fails. What must not
 * happen is a broken-image icon where an answer should be.
 *
 * Speech is unaffected. The server already strips markdown before
 * synthesis, and this module is display-only — it never rewrites the
 * text a caller stored or spoke.
 *
 * ── The second pass ────────────────────────────────────────────────────
 *
 * Splitting a message into text and pictures made a picture render. It
 * left the rest of a spatial answer as one unbroken paragraph, which is
 * the wrong shape for what those answers actually are. `overviewText` and
 * `agentsText` already emit a heading line and then bullet markers, one
 * per thing found — the structure was in the string and nothing read it.
 * So there is now a block layer above the inline one: paragraphs, and
 * lists built from the markers those builders were already writing. No
 * server change was needed, which is the sign the structure was real.
 *
 * The inline set grew too, for the same reason a contract address was
 * creating a horizontal scrollbar: a fifty-six character identifier is
 * not a word, and laying it out as one asks a 320-pixel card to be wider
 * than the screen. An `identifier` segment can be truncated on screen
 * while keeping the whole value reachable, which is what somebody
 * comparing an address against their wallet needs.
 *
 * The same discipline applies to the new kinds as to the old. An
 * identifier is one because it passes real StrKey validation — base32,
 * version byte, checksum — not because it is long and uppercase. `XLM`,
 * `USDC` and `BURJ KHALIFA` are text, and so is a real address with one
 * character changed. Everything unrecognised is still text.
 */

import {
  isStrKey,
  isTransactionHash,
  truncateIdentifier,
} from '../../utils/stellarStrKey';

export { truncateIdentifier };

/** A run of plain text. Rendered as-is. */
export interface TextSegment {
  kind: 'text';
  text: string;
}

/** A link, from `[label](href)` or a bare URL. */
export interface LinkSegment {
  kind: 'link';
  href: string;
  label: string;
}

/** An image, from `![alt](href)`. */
export interface ImageSegment {
  kind: 'image';
  href: string;
  alt: string;
}

/**
 * A machine identifier: a Stellar address, a contract, a transaction hash.
 *
 * Carries the full value and a display form, because those have to differ.
 * The full value is what somebody copies; the display form is what fits in
 * a card 320 pixels wide without pushing a scrollbar under the
 * conversation.
 */
export interface IdentifierSegment {
  kind: 'identifier';
  /** The complete value, exactly as it appeared. Never truncated. */
  value: string;
  /** The shortened form, for display only. */
  display: string;
  /** What it is, so a renderer can label it. */
  idKind: 'contract' | 'account' | 'muxed' | 'hash';
}

/** A Soroban function name, like `execute_payment`. */
export interface CodeSegment {
  kind: 'code';
  text: string;
}

/** An amount of an asset, like `100 XLM`. */
export interface AmountSegment {
  kind: 'amount';
  text: string;
}

/** An availability phrase the server produced, like `Available now`. */
export interface StatusSegment {
  kind: 'status';
  text: string;
  /** Whether this reads as a yes. Drives colour, nothing else. */
  positive: boolean;
}

/** A distance, radius or duration, like `250 m` or `30 days`. */
export interface MetricSegment {
  kind: 'metric';
  text: string;
}

/** A Stellar network name, like `testnet`. */
export interface NetworkSegment {
  kind: 'network';
  text: string;
}

export type ChatSegment =
  | TextSegment
  | LinkSegment
  | ImageSegment
  | IdentifierSegment
  | CodeSegment
  | AmountSegment
  | StatusSegment
  | MetricSegment
  | NetworkSegment;

/** A paragraph, or one item of a list. A line of segments. */
export interface ParagraphBlock {
  kind: 'paragraph';
  segments: ChatSegment[];
}

/** One item of a bullet list. */
export interface ListItem {
  segments: ChatSegment[];
  /**
   * Whether the marker said this one is available.
   *
   * `overviewText` writes a filled bullet for something you can do now and
   * a dash for something that needs a condition met first. That distinction
   * is the most useful thing in the answer and it was rendering as
   * punctuation. `null` for a list whose marker carried no such meaning.
   */
  available: boolean | null;
}

/** A bullet list, from the markers the answer builders already emit. */
export interface ListBlock {
  kind: 'list';
  items: ListItem[];
}

export type ChatBlock = ParagraphBlock | ListBlock;

/**
 * `![alt](href)` or `[label](href)`, with the leading `!` telling them
 * apart. The href stops at the first whitespace or closing paren so a
 * malformed message cannot swallow the rest of the text.
 */
const MARKDOWN_LINK = /(!?)\[([^\]]*)\]\(\s*([^\s)]+)\s*\)/g;

/**
 * A bare URL, so a model that writes one without markdown still gets a
 * link. Trailing sentence punctuation is excluded from the match:
 * "see https://example.com/a." should not link the full stop.
 */
const BARE_URL = /https:\/\/[^\s<>"']+[^\s<>"'.,;:!?)\]]/g;

/** Extensions we will attempt to render as a picture. */
const IMAGE_EXTENSION = /\.(?:png|jpe?g|gif|webp|avif|svg)(?:$|\?)/i;

/**
 * Whether a URL is safe to turn into an href.
 *
 * `https:` only, and parsed rather than pattern-matched so that
 * `https:` appearing later in a crafted string cannot pass. Anything
 * unparseable is not a URL.
 */
export function isSafeUrl(raw: string): boolean {
  try {
    return new URL(raw).protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Whether a URL looks like a picture.
 *
 * By extension, which is a guess — and the reason the renderer has a
 * fallback rather than trusting this. A content type would be authoritative
 * and needs a request we are not going to make before rendering a chat
 * bubble.
 */
export function looksLikeImage(raw: string): boolean {
  try {
    return IMAGE_EXTENSION.test(new URL(raw).pathname);
  } catch {
    return false;
  }
}

/**
 * The inline patterns, tried in this order against a run of plain text.
 *
 * Order matters where two could match the same characters. Identifiers go
 * first because a contract address is base32 and could otherwise be read as
 * a shouted word; amounts go before metrics because `100 XLM` and `100 m`
 * differ only in their unit.
 *
 * Each entry returns a segment or null. Returning null means "not this
 * after all", which is how a candidate that fails StrKey validation falls
 * through to being ordinary text rather than being forced into a shape.
 */
interface InlineRule {
  pattern: RegExp;
  build: (match: RegExpMatchArray) => ChatSegment | null;
}

/**
 * Anything that could be a StrKey or a hash, by alphabet and length only.
 *
 * Deliberately loose: the real decision is `isStrKey`, below. Matching
 * loosely and validating strictly is what lets `BURJ KHALIFA` and a
 * one-character-wrong address both end up as text.
 */
const IDENTIFIER_CANDIDATE = /\b(?:[A-Z2-7]{56,69}|[0-9a-f]{64})\b/gi;

/**
 * A Soroban function name: lowercase with an internal underscore, or a
 * bare `word()` call. The underscore is what keeps this off English.
 */
const FUNCTION_NAME = /\b[a-z][a-z0-9]*(?:_[a-z0-9]+)+\b(?:\(\s*\))?|\b[a-z][a-z0-9]*\(\s*\)/g;

/**
 * An amount with an asset code after it.
 *
 * The code is 2 to 12 uppercase characters, which is the Stellar range, and
 * the number may carry thousands separators and decimals. Anchored on the
 * number so a bare `XLM` in prose stays prose.
 */
const AMOUNT = /\b\d[\d,]*(?:\.\d+)?\s+[A-Z][A-Z0-9]{1,11}\b/g;

/** A distance, a duration or a percentage. */
const METRIC = /\b\d[\d,]*(?:\.\d+)?\s*(?:km|m|cm|mi|ft|%)\b|\b\d[\d,]*(?:\.\d+)?\s+(?:seconds?|minutes?|hours?|days?|weeks?|months?|years?|metres?|meters?)\b/g;

/** A Stellar network name. A closed set, because it is one. */
const NETWORK = /\b(?:testnet|mainnet|pubnet|futurenet|public network)\b/gi;

/**
 * The availability phrases the server actually produces.
 *
 * Taken from `REASON_TEXT` in `eventManagementModel`, so this recognises
 * what is written rather than guessing at what a status looks like. A
 * phrase not on this list is prose, which is the safe direction.
 */
const STATUS_POSITIVE = ['Available now'];
const STATUS_NEGATIVE = [
  'Not available',
  'Cannot tell right now',
  'You are too far away',
  'Connect a wallet first',
  'This is switched off',
  'The event has ended',
  'The event was cancelled',
  'The event is not running',
  'Not yet — the event has not started',
  'The rule behind this is missing',
  'You need to stay a little longer',
];

function _escapeForRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

const STATUS = new RegExp(
  [...STATUS_POSITIVE, ...STATUS_NEGATIVE].map(_escapeForRegex).join('|'),
  'g',
);

/** What kind of identifier a validated candidate is, for labelling. */
function _identifierKind(value: string): IdentifierSegment['idKind'] | null {
  if (isTransactionHash(value)) return 'hash';
  if (!isStrKey(value)) return null;
  // The discriminant is meaningful only once the value is known to be a
  // valid StrKey, which `isStrKey` has just established.
  const first = value[0];
  if (first === 'C') return 'contract';
  if (first === 'M') return 'muxed';
  if (first === 'G') return 'account';
  // A seed or another kind. Recognised so it can be truncated rather than
  // laid out in full, but not labelled as an account.
  return 'contract';
}

const INLINE_RULES: InlineRule[] = [
  {
    pattern: IDENTIFIER_CANDIDATE,
    build: (match) => {
      const value = match[0];
      const idKind = _identifierKind(value);
      // The whole point of validating: not every long base32 run is an
      // address, and the ones that are not stay text.
      if (!idKind) return null;
      return {
        kind: 'identifier',
        value,
        display: truncateIdentifier(value),
        idKind,
      };
    },
  },
  {
    pattern: STATUS,
    build: (match) => ({
      kind: 'status',
      text: match[0],
      positive: STATUS_POSITIVE.includes(match[0]),
    }),
  },
  { pattern: AMOUNT, build: (match) => ({ kind: 'amount', text: match[0] }) },
  { pattern: METRIC, build: (match) => ({ kind: 'metric', text: match[0] }) },
  { pattern: FUNCTION_NAME, build: (match) => ({ kind: 'code', text: match[0] }) },
  { pattern: NETWORK, build: (match) => ({ kind: 'network', text: match[0] }) },
];

/**
 * Expand one run of plain text through the inline rules.
 *
 * Applied rule by rule rather than with one combined pattern, because the
 * rules disagree about the same characters and the order is the tiebreak —
 * `100 XLM` has to reach the amount rule before the metric rule sees `100`.
 * Each rule runs over only what earlier rules left as text, which is what a
 * single alternation could not express.
 *
 * The regexes are cloned per call. They are module-level and carry `g`, so
 * sharing them across calls would share `lastIndex` and make the result
 * depend on what was parsed previously — a class of bug that shows up only
 * under load.
 */
function _expandInline(text: string, ruleIndex = 0): ChatSegment[] {
  if (!text) return [];
  if (ruleIndex >= INLINE_RULES.length) return [{ kind: 'text', text }];

  const rule = INLINE_RULES[ruleIndex];
  const pattern = new RegExp(rule.pattern.source, rule.pattern.flags);
  const out: ChatSegment[] = [];
  let cursor = 0;

  for (const match of text.matchAll(pattern)) {
    const at = match.index ?? 0;
    const segment = rule.build(match);
    // Null means the candidate failed validation — a long base32 run that is
    // not an address. Left for later rules and ultimately for text, with the
    // cursor deliberately not advanced.
    if (!segment) continue;
    if (at < cursor) continue; // overlapped a match already consumed

    for (const seg of _expandInline(text.slice(cursor, at), ruleIndex + 1)) out.push(seg);
    out.push(segment);
    cursor = at + match[0].length;
  }

  for (const seg of _expandInline(text.slice(cursor), ruleIndex + 1)) out.push(seg);
  return out;
}

/**
 * Append a run of text, merging with the previous one if it was also text.
 *
 * Merged so that two adjacent runs (one from a skipped match, one from the
 * gap after it) do not become two React children with a seam.
 *
 * Raw: no inline expansion. The url pass uses this so its output is not
 * expanded twice, once here and once when `parseChatMessage` assembles.
 */
function _pushRawText(out: ChatSegment[], text: string): void {
  if (!text) return;
  const last = out[out.length - 1];
  if (last && last.kind === 'text') last.text += text;
  else out.push({ kind: 'text', text });
}

/** Append a run of text, expanded into whatever inline kinds it contains. */
function _pushText(out: ChatSegment[], text: string): void {
  if (!text) return;
  for (const segment of _expandInline(text)) {
    if (segment.kind === 'text') _pushRawText(out, segment.text);
    else out.push(segment);
  }
}

/**
 * Link the bare URLs inside a run of plain text.
 *
 * Runs after the markdown pass and only over the parts it did not claim,
 * so a URL already inside `[label](href)` is not linked twice.
 */
function _withBareUrls(text: string): ChatSegment[] {
  const out: ChatSegment[] = [];
  let cursor = 0;

  for (const match of text.matchAll(BARE_URL)) {
    const href = match[0];
    const at = match.index ?? 0;
    _pushRawText(out, text.slice(cursor, at));
    cursor = at + href.length;

    if (!isSafeUrl(href)) {
      _pushRawText(out, href);
    } else if (looksLikeImage(href)) {
      out.push({ kind: 'image', href, alt: '' });
    } else {
      out.push({ kind: 'link', href, label: href });
    }
  }

  _pushRawText(out, text.slice(cursor));
  return out;
}

/**
 * Split a message into renderable segments.
 *
 * Total: anything that is not a non-empty string yields an empty list, so
 * a caller can render the result without first deciding whether there was
 * a message at all.
 *
 * An image whose URL is not safe degrades to its own alt text rather than
 * vanishing — the words were part of the answer even when the picture
 * cannot be shown.
 */
export function parseChatMessage(value: unknown): ChatSegment[] {
  if (typeof value !== 'string' || !value) return [];

  const out: ChatSegment[] = [];
  let cursor = 0;

  for (const match of value.matchAll(MARKDOWN_LINK)) {
    const [whole, bang, label, href] = match;
    const at = match.index ?? 0;

    // The text before this match still needs its bare URLs linked.
    for (const seg of _withBareUrls(value.slice(cursor, at))) {
      if (seg.kind === 'text') _pushText(out, seg.text);
      else out.push(seg);
    }
    cursor = at + whole.length;

    if (!isSafeUrl(href)) {
      // Not linkable. Keep whichever words the author wrote, and drop the
      // punctuation and the URL rather than showing either.
      _pushText(out, label || '');
      continue;
    }

    const wantsImage = bang === '!' || looksLikeImage(href);
    if (wantsImage) out.push({ kind: 'image', href, alt: label || '' });
    else out.push({ kind: 'link', href, label: label || href });
  }

  for (const seg of _withBareUrls(value.slice(cursor))) {
    if (seg.kind === 'text') _pushText(out, seg.text);
    else out.push(seg);
  }

  return out;
}

/**
 * Whether a message has anything beyond plain text in it.
 *
 * Lets a caller keep the cheap single-text-node path for the overwhelming
 * majority of messages, which have no links and no pictures.
 */
export function hasRichContent(segments: ChatSegment[]): boolean {
  return segments.some((s) => s.kind !== 'text');
}

// ── Blocks ────────────────────────────────────────────────────────────────

/**
 * The bullet markers the answer builders emit, and what each one means.
 *
 * `overviewText` writes `•` for an action available now and `–` for one that
 * needs something first. Those were rendering as punctuation at the start of
 * a run-on line, which threw away the single most useful distinction in the
 * answer. `-` and `*` are accepted because a model writes those when it
 * makes a list of its own, and they carry no availability claim.
 */
const BULLET = /^\s*([•–\-*])\s+(.*)$/;

const AVAILABILITY_OF_MARKER: Readonly<Record<string, boolean | null>> = Object.freeze({
  '•': true,
  '–': false,
  '-': null,
  '*': null,
});

/**
 * Split a message into blocks, then each block into segments.
 *
 * One line per block, deliberately. Consecutive non-bullet lines are NOT
 * joined into a paragraph: `depositsText` puts each fact on its own line —
 * what the treasury holds, whether it can accept a deposit, whether you
 * could withdraw — and gluing them together with spaces is exactly the wall
 * of text this is meant to undo. The server already decided where the breaks
 * go; this reads its decision rather than second-guessing it.
 *
 * Blank lines separate and produce nothing. Consecutive bullet lines become
 * one list, so a heading followed by four bullets renders as a heading and a
 * list rather than five paragraphs.
 *
 * Total: a non-string or empty input yields an empty list.
 */
export function parseChatBlocks(value: unknown): ChatBlock[] {
  if (typeof value !== 'string' || !value) return [];

  const blocks: ChatBlock[] = [];
  let openList: ListBlock | null = null;

  for (const line of value.split('\n')) {
    if (!line.trim()) {
      // A blank line ends a list and contributes nothing of its own.
      openList = null;
      continue;
    }

    const bullet = line.match(BULLET);
    if (bullet) {
      const [, marker, content] = bullet;
      const item: ListItem = {
        segments: parseChatMessage(content),
        available: AVAILABILITY_OF_MARKER[marker] ?? null,
      };
      if (openList) {
        openList.items.push(item);
      } else {
        openList = { kind: 'list', items: [item] };
        blocks.push(openList);
      }
      continue;
    }

    openList = null;
    blocks.push({ kind: 'paragraph', segments: parseChatMessage(line) });
  }

  return blocks;
}
